<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
      </div>

      <settings-tab />

      <div class="row">
        <financial-sidebar />

        <div class="col-xl-9">
          <div class="card mb-3">
            <div class="card-body">
              <div class="d-flex flex-wrap justify-content-between gap-3 align-items-start">
                <div>
                  <div class="dq-eyebrow">Kenya Compliance</div>
                  <h4 class="mb-1">eTIMS Tax Profile</h4>
                  <p class="text-muted mb-0">
                    Configure the company profile used for future KRA eTIMS invoice submission.
                  </p>
                </div>
                <span class="badge dq-status" :class="profile.etims_enabled ? 'dq-status-on' : 'dq-status-off'">
                  {{ profile.etims_enabled ? "Enabled" : "Disabled" }}
                </span>
              </div>
            </div>
          </div>

          <div class="card">
            <div class="card-body">
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border mb-3" role="status"></div>
                <p class="text-muted mb-0">Loading Kenya tax profile...</p>
              </div>

              <form v-else @submit.prevent="handleSave">
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Country</label>
                    <input type="text" class="form-control" v-model="profile.country_code" readonly />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">KRA PIN</label>
                    <input type="text" class="form-control" v-model="profile.kra_pin" placeholder="A123456789X" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Branch Code</label>
                    <input type="text" class="form-control" v-model="profile.branch_code" placeholder="Main / Branch code" />
                  </div>

                  <div class="col-md-8">
                    <label class="form-label">Taxpayer Name</label>
                    <input type="text" class="form-control" v-model="profile.taxpayer_name" placeholder="Registered taxpayer name" />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Environment</label>
                    <select class="form-select" v-model="profile.etims_environment">
                      <option value="sandbox">Sandbox</option>
                      <option value="production">Production</option>
                    </select>
                  </div>

                  <div class="col-12">
                    <div class="dq-readiness" :class="profile.production_ready ? 'dq-readiness-ok' : 'dq-readiness-warn'">
                      <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                        <div>
                          <h6 class="mb-1">Setup Status: {{ setupStatusLabel }}</h6>
                          <p class="mb-0 text-muted">
                            Tenant companies should use their own KRA identity and eTIMS credentials. Superadmin should support, not own, tenant tax credentials.
                          </p>
                        </div>
                        <span class="badge" :class="profile.production_ready ? 'bg-success-subtle text-success-emphasis' : 'bg-warning-subtle text-warning-emphasis'">
                          {{ profile.production_ready ? "Production Ready" : "Needs Setup" }}
                        </span>
                      </div>
                      <ul v-if="profile.missing_requirements?.length" class="mb-0 mt-3 dq-missing-list">
                        <li v-for="item in profile.missing_requirements" :key="item">{{ item }}</li>
                      </ul>
                    </div>
                  </div>

                  <div class="col-12">
                    <div class="dq-toggle-card">
                      <div>
                        <h6 class="mb-1">Enable eTIMS for this company</h6>
                        <p class="text-muted mb-0">
                          When enabled, invoices can be queued for Kenya eTIMS submission.
                        </p>
                      </div>
                      <div class="form-check form-switch m-0">
                        <input class="form-check-input" type="checkbox" v-model="profile.etims_enabled" />
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">API Base URL</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="profile.etims_api_base_url"
                      placeholder="https://..."
                      :disabled="!profile.etims_enabled"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Client ID</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="profile.etims_client_id"
                      placeholder="Provider client id"
                      :disabled="!profile.etims_enabled"
                    />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Client Secret</label>
                    <input
                      type="password"
                      class="form-control"
                      v-model="profile.etims_client_secret"
                      placeholder="Provider client secret"
                      :disabled="!profile.etims_enabled"
                    />
                    <div class="form-text" v-if="profile.has_etims_client_secret && !profile.etims_client_secret">
                      A client secret is already stored securely. Enter a new one only if you want to replace it.
                    </div>
                  </div>
                  <div class="col-md-3">
                    <label class="form-label">Device ID</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="profile.etims_device_id"
                      placeholder="Device id"
                      :disabled="!profile.etims_enabled"
                    />
                  </div>
                  <div class="col-md-3">
                    <label class="form-label">Serial No</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="profile.etims_serial_no"
                      placeholder="Serial number"
                      :disabled="!profile.etims_enabled"
                    />
                  </div>

                  <div class="col-12" v-if="profile.has_etims_client_secret">
                    <div class="form-check">
                      <input
                        id="clearEtimsSecret"
                        class="form-check-input"
                        type="checkbox"
                        v-model="profile.clear_etims_client_secret"
                        :disabled="!profile.etims_enabled"
                      />
                      <label class="form-check-label" for="clearEtimsSecret">
                        Clear stored client secret on save
                      </label>
                    </div>
                  </div>
                </div>

                <div class="dq-note mt-4">
                  <i class="ti ti-info-circle me-2"></i>
                  Sandbox is simulated in-app today. Production credentials can be stored safely now, but live provider submission still needs the final production eTIMS connector.
                </div>

                <div class="d-flex justify-content-end mt-4">
                  <button type="submit" class="btn btn-primary" :disabled="saving">
                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                    Save Kenya Tax Profile
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import layoutHeader from "@/views/layouts/layout-header.vue";
import layoutSidebar from "@/views/layouts/layout-sidebar.vue";
import settingsTab from "@/views/pages/administration/settings/settings-tab.vue";
import financialSidebar from "@/views/pages/administration/settings/financial-settings/financial-sidebar.vue";
import taxComplianceService from "@/services/finance/taxCompliance.service";
import { toast } from "@/utils/toast";

const title = "Settings";
const text = "Administration";
const text1 = "Kenya Tax Compliance";

const loading = ref(true);
const saving = ref(false);

const profile = reactive({
  country_code: "KE",
  kra_pin: "",
  taxpayer_name: "",
  branch_code: "",
  etims_enabled: false,
  etims_environment: "sandbox",
  etims_api_base_url: "",
  etims_client_id: "",
  etims_client_secret: "",
  etims_device_id: "",
  etims_serial_no: "",
  has_etims_client_secret: false,
  production_ready: false,
  setup_status: "disabled",
  missing_requirements: [],
  clear_etims_client_secret: false,
});

const setupStatusLabelMap = {
  disabled: "Disabled",
  sandbox_ready: "Sandbox Ready",
  sandbox_incomplete: "Sandbox Incomplete",
  production_ready: "Production Ready",
  production_incomplete: "Production Incomplete",
};

const setupStatusLabel = computed(
  () => setupStatusLabelMap[profile.setup_status] || "Not Configured"
);

function applyProfile(data = {}) {
  profile.country_code = data.country_code || "KE";
  profile.kra_pin = data.kra_pin || "";
  profile.taxpayer_name = data.taxpayer_name || "";
  profile.branch_code = data.branch_code || "";
  profile.etims_enabled = !!data.etims_enabled;
  profile.etims_environment = data.etims_environment || "sandbox";
  profile.etims_api_base_url = data.etims_api_base_url || "";
  profile.etims_client_id = data.etims_client_id || "";
  profile.etims_client_secret = "";
  profile.etims_device_id = data.etims_device_id || "";
  profile.etims_serial_no = data.etims_serial_no || "";
  profile.has_etims_client_secret = !!data.has_etims_client_secret;
  profile.production_ready = !!data.production_ready;
  profile.setup_status = data.setup_status || "disabled";
  profile.missing_requirements = Array.isArray(data.missing_requirements)
    ? data.missing_requirements
    : [];
  profile.clear_etims_client_secret = false;
}

async function loadProfile() {
  loading.value = true;
  try {
    const res = await taxComplianceService.getKenyaProfile();
    applyProfile(res.data || {});
  } catch (e) {
    toast.error(e?.response?.data?.message || "Failed to load Kenya tax profile");
  } finally {
    loading.value = false;
  }
}

async function handleSave() {
  saving.value = true;
  try {
    const payload = {
      ...profile,
      etims_client_secret: profile.etims_client_secret || null,
    };
    const res = await taxComplianceService.saveKenyaProfile(payload);
    applyProfile(res.data || {});
    toast.success("Kenya tax profile saved");
  } catch (e) {
    const missing = e?.response?.data?.missing_requirements;
    const suffix = Array.isArray(missing) && missing.length ? ` Missing: ${missing.join(", ")}` : "";
    toast.error((e?.response?.data?.message || "Failed to save Kenya tax profile") + suffix);
  } finally {
    saving.value = false;
  }
}

onMounted(loadProfile);
</script>

<style scoped>
.dq-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 0.72rem;
  font-weight: 700;
  color: #6b7a99;
  margin-bottom: 0.35rem;
}

.dq-status {
  font-size: 0.75rem;
  padding: 0.55rem 0.8rem;
  border-radius: 999px;
}

.dq-status-on {
  background: #e7f8ee;
  color: #0f8a47;
}

.dq-status-off {
  background: #f2f4f7;
  color: #667085;
}

.dq-toggle-card {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 1rem 1.1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #fafbff 100%);
}

.dq-note {
  background: #f8fbff;
  border: 1px solid #dbe7ff;
  color: #36527c;
  border-radius: 14px;
  padding: 0.9rem 1rem;
}

.dq-readiness {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 1rem 1.1rem;
  background: linear-gradient(180deg, #ffffff 0%, #fafbff 100%);
}

.dq-readiness-ok {
  border-color: #b7ebc6;
  background: linear-gradient(180deg, #ffffff 0%, #f4fcf7 100%);
}

.dq-readiness-warn {
  border-color: #f7deb4;
  background: linear-gradient(180deg, #ffffff 0%, #fffaf1 100%);
}

.dq-missing-list {
  padding-left: 1rem;
  color: #7a4b00;
}
</style>
